import React, { useState, useRef, useEffect } from 'react';
import { MessageSquare, X, Send, Trash2 } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import logo from './logo2.png';
import './Chatbot.css';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || window.location.origin;

const Chatbot = () => {
  const SUGGESTIONS = [
    "How do I calm interview nerves?",
    "Help me pick a career path",
    "What are the best IT interview questions?"
  ];

  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    { role: 'assistant', content: 'Hello! I am your AI assistant. How can I help you today?' }
  ]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleClearChat = () => {
    setMessages([
      { role: 'assistant', content: 'Hello! I am your AI assistant. How can I help you today?' }
    ]);
  };

  const playPopSound = () => {
    try {
      const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
      const oscillator = audioCtx.createOscillator();
      const gainNode = audioCtx.createGain();
      
      oscillator.type = 'sine';
      oscillator.frequency.setValueAtTime(800, audioCtx.currentTime);
      oscillator.frequency.exponentialRampToValueAtTime(1200, audioCtx.currentTime + 0.1);
      
      gainNode.gain.setValueAtTime(0, audioCtx.currentTime);
      gainNode.gain.linearRampToValueAtTime(0.3, audioCtx.currentTime + 0.02);
      gainNode.gain.linearRampToValueAtTime(0, audioCtx.currentTime + 0.1);
      
      oscillator.connect(gainNode);
      gainNode.connect(audioCtx.destination);
      
      oscillator.start();
      oscillator.stop(audioCtx.currentTime + 0.1);
    } catch (e) {
      console.log('Audio not supported');
    }
  };

  const handleToggleChat = (open) => {
    setIsOpen(open);
    if (open) playPopSound();
  };

  const handleSend = async (e, suggestionText = null) => {
    e?.preventDefault();
    const userMessage = suggestionText || input.trim();
    if (!userMessage || isLoading) return;

    setInput('');
    
    // Add user message to UI
    const updatedMessages = [...messages, { role: 'user', content: userMessage }];
    setMessages(updatedMessages);
    setIsLoading(true);

    try {
      // Prepare history for API
      const history = updatedMessages.slice(0, -1).map(m => ({ role: m.role, content: m.content }));
      
      const response = await fetch(`${API_BASE_URL}/api/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          message: userMessage,
          history: history
        })
      });

      if (!response.ok) throw new Error('Network response was not ok');
      const data = await response.json();
      
      setMessages(prev => [...prev, { role: 'assistant', content: data.reply }]);
      playPopSound();
    } catch (error) {
      console.error('Chat error:', error);
      setMessages(prev => [...prev, { role: 'assistant', content: 'Sorry, I am having trouble connecting to the server right now. Please try again later.' }]);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="chatbot-container">
      {isOpen && (
        <div className="chatbot-window">
          <div className="chatbot-header">
            <h3>EdVentureX Assistant</h3>
            <div style={{ display: 'flex', gap: '12px' }}>
              <button className="chatbot-close" onClick={handleClearChat} title="Clear Chat">
                <Trash2 size={18} />
              </button>
              <button className="chatbot-close" onClick={() => setIsOpen(false)} title="Close">
                <X size={20} />
              </button>
            </div>
          </div>
          
          <div className="chatbot-messages">
            {messages.map((msg, index) => (
              <div key={index} className={`message-wrapper ${msg.role}`}>
                {msg.role === 'assistant' && (
                  <img src={logo} alt="EdVentureX Avatar" className="chatbot-avatar" />
                )}
                <div className={`message ${msg.role}`}>
                  {msg.role === 'assistant' ? (
                    <ReactMarkdown>{msg.content}</ReactMarkdown>
                  ) : (
                    msg.content
                  )}
                </div>
              </div>
            ))}
            {isLoading && <div className="typing-indicator">EdVentureX Assistant is typing...</div>}
            
            {messages.length === 1 && !isLoading && (
              <div className="chatbot-suggestions">
                {SUGGESTIONS.map((sug, idx) => (
                  <button key={idx} className="suggestion-pill" onClick={(e) => handleSend(e, sug)}>
                    {sug}
                  </button>
                ))}
              </div>
            )}
            
            <div ref={messagesEndRef} />
          </div>

          <form className="chatbot-input-container" onSubmit={handleSend}>
            <input
              type="text"
              className="chatbot-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask a question..."
              disabled={isLoading}
            />
            <button type="submit" className="chatbot-send" disabled={!input.trim() || isLoading}>
              <Send size={18} />
            </button>
          </form>
        </div>
      )}

      {!isOpen && (
        <button className="chatbot-button" onClick={() => handleToggleChat(true)}>
          <MessageSquare size={24} />
        </button>
      )}
    </div>
  );
};

export default Chatbot;
